import React, { useMemo } from 'react'
import Icon from '../../components/Icon'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../../components/DataTable'
import { Race, Grade } from '../../../types'

interface ImportError {
  index: number
  error: string
  student: any
  rawRow?: any // Optional since you might not have this yet
}

interface ErrorsProps {
  errors: ImportError[]
  setActiveTab: (tab: string) => void
}

const Errors: React.FC<ErrorsProps> = ({ errors, setActiveTab }) => {
  // Define columns for the errors table
  const columns = useMemo<ColumnDef<ImportError>[]>(
    () => [
      {
        accessorKey: 'index',
        header: 'Row #',
        cell: (info) => (
          <div className="text-sm font-medium text-gray-900">
            {(info.getValue() as number) + 1}
          </div>
        ),
      },
      {
        accessorKey: 'student',
        header: 'Student Name',
        cell: (info) => {
          const student = info.getValue() as any
          const rawRow = info.row.original.rawRow

          // Try to get name from student data first, then fallback to raw row if available
          const firstName =
            student?.firstName !== 'N/A'
              ? student?.firstName
              : rawRow?.firstName || rawRow?.['First Name'] || 'N/A'
          const lastName =
            student?.lastName !== 'N/A'
              ? student?.lastName
              : rawRow?.lastName || rawRow?.['Last Name'] || 'N/A'

          return (
            <div className="text-sm text-gray-900">
              {firstName} {lastName}
            </div>
          )
        },
      },
      {
        accessorKey: 'student.school',
        header: 'School',
        cell: (info) => {
          const student = info.row.original.student
          const rawRow = info.row.original.rawRow

          const school =
            student?.school !== 'N/A'
              ? student?.school
              : rawRow?.school || rawRow?.School || 'N/A'

          return <div className="text-sm text-gray-900">{school}</div>
        },
      },
      {
        accessorKey: 'student.grade',
        header: 'Grade',
        cell: (info) => {
          const student = info.row.original.student
          const rawRow = info.row.original.rawRow

          const grade =
            student?.grade !== 'N/A'
              ? student?.grade
              : rawRow?.grade || rawRow?.Grade || 'N/A'

          return <div className="text-sm text-gray-900">{grade}</div>
        },
      },
      {
        accessorKey: 'error',
        header: 'Error Message',
        cell: (info) => {
          const errorMessage = info.getValue() as string

          // Make error messages more user-friendly
          const formatErrorMessage = (error: string) => {
            // Handle validation errors with field paths like "students[5].race"
            if (error.includes('must be one of')) {
              const match = error.match(
                /"([^"]+)"\s+must be one of\s+\[([^\]]+)\]/,
              )
              if (match) {
                const [, fieldPath, validValues] = match
                const fieldName = fieldPath.includes('.')
                  ? fieldPath.split('.').pop()
                  : fieldPath
                return `Invalid ${fieldName}: Must be one of: ${validValues}`
              }
            }

            // Handle required field errors
            if (error.includes('required')) {
              return error.replace(
                /Path\s+`([^`]+)`\s+is required/,
                'Required field: $1',
              )
            }

            return error
          }

          return (
            <div className="text-sm text-red-600 max-w-md">
              <span className="font-medium">Error:</span>{' '}
              {formatErrorMessage(errorMessage)}
            </div>
          )
        },
      },
      // Add raw data column if rawRow data is available
      ...(errors.some((e) => e.rawRow)
        ? [
            {
              id: 'rawData',
              header: 'Raw Data',
              cell: (info: any) => {
                const rawRow = info.row.original.rawRow

                if (!rawRow || Object.keys(rawRow).length === 0) {
                  return (
                    <div className="text-xs text-gray-400">
                      No raw data available
                    </div>
                  )
                }

                return (
                  <div className="text-xs text-gray-500">
                    <details className="cursor-pointer">
                      <summary className="font-medium hover:text-gray-700">
                        View Raw Data
                      </summary>
                      <div className="mt-2 p-2 bg-gray-50 rounded border text-xs font-mono whitespace-pre-wrap max-h-32 overflow-y-auto">
                        {Object.entries(rawRow).map(([key, value]) => (
                          <div key={key} className="mb-1">
                            <span className="font-semibold text-gray-600">
                              {key}:
                            </span>{' '}
                            {String(value)}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )
              },
            },
          ]
        : []),
    ],
    [errors],
  )

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Import Errors
        </h3>
        <p className="text-gray-600">
          {errors.length > 0
            ? `${errors.length} students failed to import. Review the errors below and fix the data before retrying.`
            : 'No import errors found.'}
        </p>
      </div>

      <DataTable
        data={errors}
        columns={columns}
        searchPlaceholder="Search errors..."
        emptyStateTitle="No import errors"
        emptyStateDescription="All students were imported successfully, or no import has been attempted yet."
        emptyStateIcon={
          <Icon type="check-circle" className="mx-auto h-12 w-12 text-gray-400" />
        }
        showCreateButton={false}
      />

      {errors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Icon type="warning" className="h-5 w-5 text-amber-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Import Issues Found
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>
                  Please review the errors above and correct the data in your
                  source file. Common issues include:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>
                    Invalid race values (use: {Object.values(Race).join(', ')})
                  </li>
                  <li>Invalid or missing school names</li>
                  <li>
                    Invalid grade formats (use:{' '}
                    {Object.values(Grade).join(', ')})
                  </li>
                  <li>Missing required fields (first name, last name)</li>
                  <li>Invalid accommodation types</li>
                  <li>Insufficient permissions for specific schools</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => setActiveTab('import')}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Back to Import
        </button>
      </div>
    </div>
  )
}

export default Errors
